/**
 * Tracks how long a student stays in each high-level consensus phase
 * (Solo Time / Team Time) so phase boundaries can be logged with a duration —
 * the same enter/exit + durationMs shape used by the waiting-room funnel.
 *
 * Pure and timestamp-injected (no Date.now() inside) so the timing is trivially
 * unit-testable with explicit values. The hook owns one instance via a ref and
 * feeds it phases (already mapped to SOLO_TIME / TEAM_TIME) plus a `now`.
 */
// Consensus EVENT_STATEs that belong to the Solo Time phase. Everything else
// (discuss, solution, explanation, complete, feedback_analysis) is Team Time.
const SOLO_PHASE_STATES = new Set(["challenge", "gather"]);

/**
 * True when a consensus event state belongs to the Solo Time phase. Kept here
 * (pure, unit-tested) so the phase boundary used for dwell tracking is verified.
 */
export const isSoloPhaseState = (state) => SOLO_PHASE_STATES.has(state);

export const createPhaseDwellTracker = () => {
  let currentPhase = null;
  let enteredAt = null;

  return {
    /**
     * Record the phase the student is now in. Returns
     * `{ completedPhase, nextPhase, durationMs }` when a phase boundary is
     * crossed (so the caller can log the just-finished phase), or `null` when
     * the phase is unchanged or this is the very first phase.
     */
    transition(phase, now) {
      if (phase === currentPhase) return null;

      const completed =
        currentPhase === null
          ? null
          : { completedPhase: currentPhase, nextPhase: phase, durationMs: now - enteredAt };

      currentPhase = phase;
      enteredAt = now;
      return completed;
    },

    /**
     * Flush the final phase when the activity ends/unmounts. Returns
     * `{ completedPhase, nextPhase: null, durationMs }` once, or `null` if no
     * phase was ever entered.
     */
    end(now) {
      if (currentPhase === null || enteredAt === null) return null;

      const completed = { completedPhase: currentPhase, nextPhase: null, durationMs: now - enteredAt };
      currentPhase = null;
      enteredAt = null;
      return completed;
    },
  };
};
